import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useDebounce } from "../../../lib/hooks/useDebounce";
import { usePaginationHelper } from "../../../lib/hooks/usePaginationHelper";
import { useMediaQuery } from "../../../lib/hooks/useMediaQuery";
import { trimText } from "../../../lib/helpers/trimText";
import {
  normalizeSortString,
  trimOrEmpty,
} from "../../../lib/helpers/normalizeString";

import { Calendar } from "../../../api/grpc/calendarprovider/calendarprovider";
import { PaginationContext } from "../../../lib/context/Pagination/PaginationContext";
import { CalendarTypeParam } from "../../../views/Manage/Calendars/CalendarSingle/CalendarSingle";
import { Pagination } from "../../shared/Pagination/Pagination";
import {
  StructureTable,
  StructureTableColumn,
  StructureTableRows,
} from "../../shared/StructureTable/StructureTable";
import {
  sortStructureTableData,
  SortDirection,
} from "../../shared/StructureTable/sortUtils";
import { CenteredBox } from "../../shared/CenteredBox/CenteredBox";
import { ResourceFilter } from "../../shared/ResourceFilter/ResourceFilter";
import { CalendarsFilters } from "./CalendarsFilters";
import { Tooltip } from "../../shared/Tooltip/Tooltip";

interface Props {
  data: Calendar[];
  calendarType: CalendarTypeParam;
}
export const ListCalendars = ({ data, calendarType }: Props) => {
  const smallScreen = useMediaQuery("(max-width: 1200px)");
  const { page, pageSize, pagination } = usePaginationHelper(data.length);

  const [searchValue, setSearchValue] = useState("");
  const [hasRoom, setHasRoom] = useState<boolean | undefined>(undefined);
  const debouncedValue = useDebounce<string>(searchValue, 500);
  const [sortedColumnKey, setSortedColumnKey] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const indexOfLastItem = page * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const columns: StructureTableColumn[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortKey: "sortMeta.name",
    },
    {
      key: "email",
      header: "Email",
      hidden: calendarType.toLowerCase() === "gsuite",
      sortable: true,
      sortKey: "sortMeta.email",
    },
    {
      key: "room",
      header: "Room",
      sortable: true,
      sortKey: "sortMeta.roomSort",
    },
  ];

  const showPagination = !!!debouncedValue.length && hasRoom === undefined;

  const filteredCalendars = useMemo(() => {
    let items = data || [];

    if (hasRoom !== undefined) {
      items = items.filter((calendar) =>
        hasRoom ? !!calendar.room : !calendar.room
      );
    }

    if (!!debouncedValue.length) {
      const searchTerm = debouncedValue.toLowerCase();
      items = items.filter((calendar) =>
        calendar.name.toLowerCase().includes(searchTerm)
      );
    }

    return items;
  }, [data, debouncedValue, hasRoom]);

  const calendarsWithSortMeta = useMemo(
    () =>
      filteredCalendars.map((calendar) => ({
        ...calendar,
        sortMeta: {
          name: trimOrEmpty(calendar.name),
          email: trimOrEmpty(calendar.email),
          room: trimOrEmpty(calendar.room?.name),
          // special request: unassigned rooms (named as -) should always be at the bottom when sorting by A-Z, and vice versa
          roomSort: calendar.room
            ? `0|${normalizeSortString(calendar.room.name)}`
            : "1|",
        },
      })),
    [filteredCalendars]
  );

  const sortedCalendars = useMemo(
    () =>
      sortStructureTableData(
        calendarsWithSortMeta,
        columns,
        sortedColumnKey,
        sortDirection
      ),
    [calendarsWithSortMeta, columns, sortedColumnKey, sortDirection]
  );

  const currentCalendarsData = useMemo(
    () => sortedCalendars.slice(indexOfFirstItem, indexOfLastItem),
    [indexOfFirstItem, indexOfLastItem, sortedCalendars]
  );

  const displayCalendars = showPagination
    ? currentCalendarsData
    : sortedCalendars;

  const rows: StructureTableRows[] = displayCalendars.map((item) => ({
    name: (
      <h4 className="CalendarAccountsList__name">
        <Tooltip content={item.name}>
          {trimText(item.name, smallScreen ? 200 : 35)}
        </Tooltip>
      </h4>
    ),
    email: (
      <h4 className="CalendarAccountsList__email">
        <Tooltip content={item.email}>
          {trimText(item.email, smallScreen ? 200 : 32)}
        </Tooltip>
      </h4>
    ),
    room: item?.room ? (
      <NavLink
        to={`/manage/organization/room/${item.room?.id}`}
        className="CalendarAccountsList__status"
      >
        {trimText(item.room?.name || "-")}
      </NavLink>
    ) : (
      <h4 className="CalendarAccountsList__status">-</h4>
    ),
  }));

  const handleSort = (columnKey: string) => {
    pagination.setPage(1);

    if (columnKey === sortedColumnKey) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    setSortedColumnKey(columnKey);
    setSortDirection("desc");
  };

  return (
    <PaginationContext.Provider value={pagination}>
      <ResourceFilter
        searchValue={searchValue}
        onChangeSearch={(e) => setSearchValue(e.target.value)}
        clearSearchValue={() => setSearchValue("")}
        numberOfFilters={hasRoom !== undefined ? 1 : undefined}
      >
        <CalendarsFilters
          assignedFilter={hasRoom}
          handleAssignedChange={(e) => {
            setHasRoom(e);
          }}
        />
      </ResourceFilter>

      <StructureTable
        rows={rows}
        columns={columns}
        noResultsPlaceholder={<CenteredBox>No calendar found</CenteredBox>}
        onSort={handleSort}
        sortedColumnKey={sortedColumnKey}
        sortDirection={sortDirection}
      />
      {showPagination && <Pagination />}
    </PaginationContext.Provider>
  );
};
